import PropTypes from 'prop-types';
import { useRef, useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import ButtonBase from '@mui/material/ButtonBase';
import CardContent from '@mui/material/CardContent';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import ProfileTab from './ProfileTab';
import SettingTab from './SettingTab';
import Avatar from 'components/@extended/Avatar';
import MainCard from 'components/MainCard';
import Transitions from 'components/@extended/Transitions';
import IconButton from 'components/@extended/IconButton';
import { useAuth } from 'contexts/AuthContext';
import { formatUserBadgeName } from 'utils/userDisplayName';

// assets
import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import SettingOutlined from '@ant-design/icons/SettingOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';
import logoImage from 'assets/images/users/logovrp1.png';

// tab panel wrapper
function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`profile-tabpanel-${index}`} aria-labelledby={`profile-tab-${index}`} {...other}>
      {value === index && children}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `profile-tab-${index}`,
    'aria-controls': `profile-tabpanel-${index}`
  };
}

// ==============================|| HEADER CONTENT - PROFILE ||============================== //

export default function Profile() {
  const theme = useTheme();
  const { user, logout } = useAuth();

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const [value, setValue] = useState(0);

  const handleChange = (_event, newValue) => {
    setValue(newValue);
  };

  const handleLogoutClick = () => {
    logout();
    setOpen(false);
  };

  const ROLE_COLOR_MAP = {
    admin: 'error.main',
    supervisor: 'warning.dark',
    comercial: '#0f5132',
    operario: '#0083c6',
    contabilidad: '#2ecc71'
  };

  const ROLE_BADGE_BG_MAP = {
    admin: 'error.light',
    supervisor: 'warning.light',
    comercial: '#d1fae5',
    operario: '#d6ecf8',
    contabilidad: '#e3f7ea'
  };

  const ROLE_LABEL_MAP = {
    admin: 'Admin',
    supervisor: 'Supervisor',
    comercial: 'Comercial',
    operario: 'Operario',
    contabilidad: 'Contabilidad'
  };

  const getRoleColor = (role) => ROLE_COLOR_MAP[role] || 'text.secondary';
  const getRoleBackgroundColor = (role) => ROLE_BADGE_BG_MAP[role] || 'grey.100';
  const getRoleText = (role) => ROLE_LABEL_MAP[role] || role || 'Sin rol';
  const userDisplayName = formatUserBadgeName(user);

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <ButtonBase
        sx={(theme) => ({
          p: 0.25,
          bgcolor: open ? 'grey.100' : 'transparent',
          borderRadius: 1,
          '&:hover': { bgcolor: 'secondary.lighter' },
          '&:focus-visible': { outline: `2px solid ${theme.palette.secondary.dark}`, outlineOffset: 2 },
          ...theme.applyStyles('dark', { bgcolor: open ? 'background.default' : 'transparent', '&:hover': { bgcolor: 'secondary.light' } })
        })}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Stack direction="row" sx={{ gap: 1.25, alignItems: 'center', p: 0.5 }}>
          <Avatar
            alt="VRP Logo"
            src={logoImage}
            size="sm"
            sx={{
              bgcolor: 'background.paper',
              border: '2px solid',
              borderColor: 'primary.light',
              width: 40,
              height: 40,
              '& img': {
                objectFit: 'contain',
                padding: '2px'
              }
            }}
          />
          <Stack direction="column" sx={{ alignItems: 'flex-start' }}>
            <Typography variant="subtitle1" sx={{ textTransform: 'capitalize', lineHeight: 1.2, fontWeight: 600 }}>
              {userDisplayName}
            </Typography>
            <Box
              sx={{
                bgcolor: getRoleBackgroundColor(user?.role),
                color: getRoleColor(user?.role),
                px: 1.5,
                py: 0.25,
                borderRadius: 1,
                fontSize: '0.7rem',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                border: '1px solid',
                borderColor: getRoleColor(user?.role),
                minWidth: 96,
                textAlign: 'center',
                whiteSpace: 'nowrap'
              }}
            >
              {getRoleText(user?.role)}
            </Box>
          </Stack>
        </Stack>
      </ButtonBase>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 9]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
            <Paper sx={(theme) => ({ boxShadow: theme.customShadows.z1, width: 290, minWidth: 240, maxWidth: { xs: 250, md: 290 } })}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard elevation={0} border={false} content={false}>
                  <CardContent sx={{ px: 2.5, pt: 3 }}>
                    <Grid container justifyContent="space-between" alignItems="center">
                      <Grid>
                        <Stack direction="row" sx={{ gap: 1.25, alignItems: 'center' }}>
                          <Avatar
                            alt="VRP Logo"
                            src={logoImage}
                            sx={{
                              width: 48,
                              height: 48,
                              bgcolor: 'background.paper',
                              border: '3px solid',
                              borderColor: 'primary.light',
                              '& img': {
                                objectFit: 'contain',
                                padding: '3px'
                              }
                            }}
                          />
                          <Stack>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {userDisplayName}
                            </Typography>
                            <Box
                              sx={{
                                bgcolor: getRoleBackgroundColor(user?.role),
                                color: getRoleColor(user?.role),
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                fontSize: '0.75rem',
                                fontWeight: 'bold',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                border: '1px solid',
                                borderColor: getRoleColor(user?.role),
                                minWidth: '80px',
                                textAlign: 'center',
                                display: 'inline-block'
                              }}
                            >
                              {getRoleText(user?.role)}
                            </Box>
                          </Stack>
                        </Stack>
                      </Grid>
                      <Grid>
                        <Tooltip title="Logout">
                          <IconButton size="large" sx={{ color: 'text.primary' }} onClick={handleLogoutClick}>
                            <LogoutOutlined />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </CardContent>

                  <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs variant="fullWidth" value={value} onChange={handleChange} aria-label="profile tabs">
                      <Tab
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'center',
                          alignItems: 'center',
                          textTransform: 'capitalize',
                          gap: 1.25,
                          '& .MuiTab-icon': {
                            marginBottom: 0
                          }
                        }}
                        icon={<UserOutlined />}
                        label="Profile"
                        {...a11yProps(0)}
                      />
                      <Tab
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'center',
                          alignItems: 'center',
                          textTransform: 'capitalize',
                          gap: 1.25,
                          '& .MuiTab-icon': {
                            marginBottom: 0
                          }
                        }}
                        icon={<SettingOutlined />}
                        label="Setting"
                        {...a11yProps(1)}
                      />
                    </Tabs>
                  </Box>
                  <TabPanel value={value} index={0} dir={theme.direction}>
                    <ProfileTab handleLogout={handleLogoutClick} />
                  </TabPanel>
                  <TabPanel value={value} index={1} dir={theme.direction}>
                    <SettingTab />
                  </TabPanel>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}

TabPanel.propTypes = { children: PropTypes.node, value: PropTypes.number, index: PropTypes.number, other: PropTypes.any };
